import { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Heart, Server, Sparkles, ShieldCheck, CheckCircle2, Users, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const TEAL = "#0D9488";
const PRESETS = [5, 25, 50];

// Icon lookup used by editable "Where your dollars go" bullets — the admin
// picks one of these keys per bullet in the Support Page editor.
const WHY_ICONS = { server: Server, sparkles: Sparkles, shield: ShieldCheck };

/**
 * /support — "Support the Exchange" donation page (Venmo-only, one-time).
 *
 * All visible wording is loaded from `/api/support-page/content` — see
 * Admin → Support Page editor. The Venmo flow, presets, wall, and badge
 * logic are intentionally *not* editable here; only copy.
 */
export default function Support() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [content, setContent] = useState(null);
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [confirmed, setConfirmed] = useState(null); // {amount, kind}
  const [me, setMe] = useState(null);
  const [wall, setWall] = useState(null);

  useEffect(() => {
    api.get("/donations/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ enabled: false }));
    api.get("/donations/me").then(({ data }) => setMe(data)).catch(() => {});
    api.get("/donations/wall").then(({ data }) => setWall(data)).catch(() => setWall({ supporters: [] }));
    // Preview mode: staff can hit /support?preview=draft to see the unpublished
    // draft. Non-staff hitting the URL falls back to the public content because
    // /admin/support-page requires admin.
    const params = new URLSearchParams(window.location.search);
    const isPreview = params.get("preview") === "draft";
    if (isPreview) {
      api.get("/admin/support-page")
        .then(({ data }) => setContent(data.draft))
        .catch(() => api.get("/support-page/content").then(({ data }) => setContent(data)).catch(() => setContent(null)));
    } else {
      api.get("/support-page/content").then(({ data }) => setContent(data)).catch(() => setContent(null));
    }
  }, []);

  // Effective amount: preset unless the user typed a custom number.
  const effAmount = (() => {
    const c = Number(custom);
    if (custom !== "" && !Number.isNaN(c) && c > 0) return Math.round(c * 100) / 100;
    return amount;
  })();

  const validAmount = effAmount >= 1 && effAmount <= (config?.max_amount || 5000);

  return (
    <AppLayout>
      {new URLSearchParams(window.location.search).get("preview") === "draft" && (
        <div
          className="mb-4 rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 flex items-center justify-between gap-3 flex-wrap"
          data-testid="support-preview-banner"
        >
          <span className="flex items-center gap-2 font-semibold">
            <Eye className="w-4 h-4" /> Preview mode — showing unpublished draft
          </span>
          <a href="/admin/support-page" className="text-xs font-semibold underline">Back to editor</a>
        </div>
      )}
      <PageHero
        eyebrow="Community"
        title={content?.page_title || "Support the Exchange"}
        subtitle={content?.page_subtitle || "Every dollar keeps servers humming, pays for AI-parsed meals, and funds new features. Give what you can — every amount matters."}
        icon={<Heart className="w-8 h-8" style={{ color: TEAL }} />}
        gradient
      />

      <div data-testid="support-page" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Donation form */}
        <div className="lg:col-span-2 space-y-6">
          {confirmed ? (
            <Card className="border border-teal-300 bg-teal-50/70 rounded-xl p-8 text-center" data-testid="support-thank-you">
              <div className="flex justify-center mb-3">
                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center" style={{ color: TEAL }}>
                  <CheckCircle2 className="w-8 h-8" strokeWidth={2.2} />
                </div>
              </div>
              <div className="text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: TEAL }}>Payment confirmed</div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">{content?.thank_you_title?.replace("{name}", user?.name?.split(" ")[0] || "friend") || `Thank you, ${user?.name?.split(" ")[0] || "friend"}.`}</h2>
              <p className="text-slate-700 mb-1">You just gave <b>${Number(confirmed.amount).toFixed(2)}</b> to keep this project alive.</p>
              <p className="text-slate-600 text-sm">{content?.thank_you_body || "A Supporter badge has been added to your dashboard."}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <Button
                  onClick={() => { setConfirmed(null); setCustom(""); setAmount(25); }}
                  className="bg-white hover:bg-slate-50 text-teal-700 border border-teal-300 rounded-none uppercase tracking-widest text-xs font-semibold h-10 px-4"
                  data-testid="support-donate-again"
                >
                  Give again
                </Button>
                <a href="/dashboard" className="inline-flex items-center h-10 px-4 rounded-none uppercase tracking-widest text-xs font-semibold text-white" style={{ background: TEAL }}>
                  Back to dashboard
                </a>
              </div>
            </Card>
          ) : (
            <Card className="border border-slate-200 bg-white rounded-xl p-6" data-testid="support-donation-card">
              <div className="text-[11px] font-semibold uppercase tracking-widest mb-3" style={{ color: TEAL }}>{content?.donation_eyebrow || "Pick an amount"}</div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {PRESETS.map((v) => {
                  const active = amount === v && custom === "";
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => { setAmount(v); setCustom(""); }}
                      className={`h-14 rounded-lg border-2 transition-colors font-semibold text-lg ${active ? "border-teal-500 bg-teal-50 text-teal-800" : "border-slate-200 bg-white text-slate-700 hover:border-teal-300"}`}
                      data-testid={`support-preset-${v}`}
                    >
                      ${v}
                    </button>
                  );
                })}
              </div>

              <div className="mb-4">
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Or enter a custom amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
                  <Input
                    type="number"
                    min="1"
                    max="5000"
                    step="1"
                    placeholder="Custom amount"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    className="h-12 pl-7 rounded-lg bg-white border-slate-200 tabular-nums"
                    data-testid="support-custom-amount"
                  />
                </div>
                {(() => {
                  if (custom === "") return null;
                  const n = Number(custom);
                  const bad = Number.isNaN(n) || n <= 0 || n > (config?.max_amount || 5000);
                  return bad ? (
                    <div className="text-xs text-red-600 mt-1">Please enter an amount between $1 and $5,000.</div>
                  ) : null;
                })()}
              </div>

              <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 mb-4 flex items-baseline justify-between">
                <div className="text-sm text-slate-600 uppercase tracking-widest text-[11px] font-semibold">{content?.donation_amount_label || "You are giving"}</div>
                <div className="font-bold text-2xl tabular-nums text-slate-900" data-testid="support-eff-amount">${effAmount.toFixed(2)}</div>
              </div>

              {(() => {
                const note = encodeURIComponent(`Amino Exchange donation`);
                const venmoWeb = `https://venmo.com/aminoexchange?txn=pay&amount=${effAmount.toFixed(2)}&note=${note}`;
                const venmoApp = `venmo://paycharge?txn=pay&recipients=aminoexchange&amount=${effAmount.toFixed(2)}&note=${note}`;
                return (
                  <>
                    {validAmount ? (
                      <a
                        href={venmoWeb}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          // Try to open the Venmo app first on mobile; browser falls back to venmoWeb automatically.
                          if (/iPhone|iPad|Android/i.test(navigator.userAgent)) {
                            window.location.href = venmoApp;
                          }
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg font-bold uppercase tracking-widest text-sm text-white transition-colors"
                        style={{ background: "#3D95CE" }}
                        data-testid="support-venmo-btn"
                      >
                        Pay ${effAmount.toFixed(2)} with Venmo → @aminoexchange
                      </a>
                    ) : (
                      <div className="text-sm text-slate-500 text-center py-3">Enter a valid amount to continue.</div>
                    )}
                    <p className="text-[11px] text-slate-500 text-center mt-3 leading-relaxed">
                      {content?.donation_cta_hint || "Opens the Venmo app (or venmo.com) with the amount pre-filled. After you send, drop a quick note so we can add you to the Supporter Wall."}
                    </p>
                    {content?.paypal_enabled && content?.footer_note && (
                      <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-500 text-center" data-testid="support-footer-note">
                        {content.footer_note}
                      </div>
                    )}
                  </>
                );
              })()}

              <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5" style={{ color: TEAL }} />
                Sent directly through Venmo · no card details ever touch our servers.
              </div>
            </Card>
          )}

          {/* Wall opt-in — only for existing supporters */}
          {me?.is_supporter && !confirmed && (
            <Card className="border border-slate-200 bg-white rounded-xl p-4 flex items-center gap-3" data-testid="wall-opt-card">
              {me.show_on_wall
                ? <Eye className="w-5 h-5 shrink-0" style={{ color: TEAL }} />
                : <EyeOff className="w-5 h-5 text-slate-400 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">Show my first name on the Supporter Wall</div>
                <div className="text-xs text-slate-500">Social proof helps others give — only your first name + a rough amount chip is shown.</div>
              </div>
              <Button
                onClick={async () => {
                  try {
                    const { data } = await api.post("/donations/wall-opt", { show_on_wall: !me.show_on_wall });
                    setMe((m) => ({ ...m, show_on_wall: data.show_on_wall }));
                    api.get("/donations/wall").then(({ data }) => setWall(data)).catch(() => {});
                    toast.success(data.show_on_wall ? "You're on the wall 💚" : "Removed from the wall");
                  } catch (err) { toast.error(formatApiError(err)); }
                }}
                className={`rounded-none uppercase tracking-widest text-xs font-semibold h-9 px-3 ${me.show_on_wall ? "bg-slate-100 hover:bg-slate-200 text-slate-800" : "bg-[#0D9488] hover:bg-[#0F766E] text-white"}`}
                data-testid="wall-opt-toggle"
              >
                {me.show_on_wall ? "Hide me" : "Show me"}
              </Button>
            </Card>
          )}
        </div>

        {/* Right column: The Why */}
        <div className="space-y-4">
          <Card className="border border-slate-200 bg-white rounded-xl p-6" data-testid="support-why-card">
            <div className="text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: TEAL }}>{content?.why_give_eyebrow || "Why give"}</div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">{content?.why_give_heading || "Where your dollars go"}</h3>
            <ul className="space-y-3 text-sm text-slate-700">
              {(content?.why_give_items?.length ? content.why_give_items : [
                { icon: "server",   title: "Servers & database.",  body: "Every countdown, streak, and AI parse runs on infrastructure that costs real money each month." },
                { icon: "sparkles", title: "New features.",         body: "Meal photo memory, combined health reports, and re-consent flows all shipped in the last few weeks — your support funds the next batch." },
                { icon: "shield",   title: "Kept ad-free.",         body: "No trackers, no data sales, no sponsor pop-ups. Ever." },
              ]).map((item, i) => {
                const Icon = WHY_ICONS[item.icon] || Server;
                return (
                  <li key={`why-${i}`} className="flex gap-2.5" data-testid={`support-why-item-${i}`}>
                    <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: TEAL }} />
                    <span><b>{item.title}</b> {item.body}</span>
                  </li>
                );
              })}
            </ul>
          </Card>

          <Card className="border border-teal-200 bg-teal-50/60 rounded-xl p-5" data-testid="support-badge-explainer">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-4 h-4" style={{ color: TEAL }} />
              <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: TEAL }}>{content?.supporter_badge_title || "Supporter badge"}</div>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              {content?.supporter_badge_body || "Anyone who chips in — no matter the amount — gets a permanent Supporter badge on their dashboard. It's a small \"thank you\" we bake right into the app."}
            </p>
          </Card>
        </div>
      </div>

      {/* Public Supporter Wall — social proof at the bottom of the page */}
      {wall && wall.supporters && wall.supporters.length > 0 && (
        <div className="mt-8" data-testid="supporter-wall">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5" style={{ color: TEAL }} />
            <h2 className="text-xl font-bold text-slate-900">Wall of supporters</h2>
            <span className="text-xs text-slate-500 font-mono-num uppercase tracking-widest">
              {wall.count} strong
            </span>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Real people keeping this app alive. Only first names + rough amount ranges are shown.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {wall.supporters.map((s, i) => (
              <div
                key={`${s.first_name}-${i}`}
                className="flex items-center gap-2 border border-slate-200 bg-white hover:border-teal-300 transition-colors px-3 py-2 rounded-lg"
                data-testid={`wall-supporter-${i}`}
              >
                <Heart className="w-3.5 h-3.5 shrink-0" style={{ color: TEAL }} fill="currentColor" />
                <span className="text-sm font-semibold text-slate-800 truncate flex-1">{s.first_name}</span>
                <span className="font-mono-num text-[9px] uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded">
                  {s.bucket}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
